import { computed, toValue } from 'vue';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/vue-query';
import type {
  MaterialSwatch,
  MaterialSwatchCreate,
} from '@cutr/constants/material-swatch';
import {
  assignMaterialSwatch,
  createMaterialSwatch,
  deleteMaterialSwatch,
  getMaterialSwatchesForOrder,
  unassignMaterialSwatch,
  updateMaterialSwatch,
} from '@/api/materialSwatches';
import { materialSwatchKeys } from './keys';
import { useInvalidateParts } from './parts';
import { patchRow, prependRow, removeRow } from './shared/listUpdaters';
import { useInvalidateScoped } from './shared/useInvalidateScoped';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags, WithVariant } from './shared/types';

export function materialSwatchesOptions(orderId: string) {
  return queryOptions({
    queryKey: materialSwatchKeys.list(orderId),
    queryFn: ({ signal }) => getMaterialSwatchesForOrder(orderId, signal),
  });
}

export function useOrderMaterialSwatchesQuery(
  orderId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(orderId, materialSwatchesOptions, flags);

  return { materialSwatches: computed(() => query.data.value ?? []), query };
}

export function useInvalidateMaterialSwatches() {
  return useInvalidateScoped(materialSwatchKeys.list);
}

export type MaterialSwatchIdVariables = { id: string; orderId: string };

export type UpdateMaterialSwatchVariables = {
  id: string;
  payload: Partial<MaterialSwatch>;
};

export type MaterialSwatchPartsVariables = { id: string; partIds: string[] };

type PartsVariables = WithVariant<
  MaterialSwatchPartsVariables & MaterialSwatchIdVariables
>;

export function useMaterialSwatchMutations(scope: {
  orderId: MaybeId;
  variantId: MaybeId;
}) {
  const queryClient = useQueryClient();
  const invalidateParts = useInvalidateParts();

  const setList = (
    orderId: string,
    updater: (
      list: MaterialSwatch[] | undefined
    ) => MaterialSwatch[] | undefined
  ) =>
    queryClient.setQueryData<MaterialSwatch[]>(
      materialSwatchKeys.list(orderId),
      updater
    );

  const invalidateList = (orderId: string) =>
    queryClient.invalidateQueries({
      queryKey: materialSwatchKeys.list(orderId),
    });

  const create = useMutation({
    mutationFn: ({
      orderVariantId,
      ...payload
    }: WithVariant<MaterialSwatchCreate>) => createMaterialSwatch(payload),
    onSuccess(newSwatch, { partIds, orderVariantId }) {
      setList(newSwatch.orderId, prependRow(newSwatch));
      if (partIds?.length) return invalidateParts(orderVariantId);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: WithVariant<UpdateMaterialSwatchVariables>) =>
      updateMaterialSwatch(id, payload),
    onSuccess(updatedSwatch, { orderVariantId }) {
      setList(updatedSwatch.orderId, patchRow<MaterialSwatch>(updatedSwatch));
      return invalidateParts(orderVariantId);
    },
  });

  const remove = useMutation({
    mutationFn: ({ id }: MaterialSwatchIdVariables) => deleteMaterialSwatch(id),
    onSuccess(_, { id, orderId }) {
      setList(orderId, removeRow(id));
    },
  });

  const assign = useMutation({
    mutationFn: ({ id, partIds }: PartsVariables) =>
      assignMaterialSwatch(id, partIds),
    onSuccess(swatch, { orderVariantId }) {
      return Promise.all([
        invalidateList(swatch.orderId),
        invalidateParts(orderVariantId),
      ]);
    },
  });

  const unassign = useMutation({
    mutationFn: ({ id, partIds }: PartsVariables) =>
      unassignMaterialSwatch(id, partIds),
    onSuccess(_, { orderId, orderVariantId }) {
      return Promise.all([
        invalidateList(orderId),
        invalidateParts(orderVariantId),
      ]);
    },
  });

  const openOrder = () => toValue(scope.orderId) ?? '';
  const activeVariant = () => toValue(scope.variantId) ?? '';

  return {
    createSwatch: (payload: MaterialSwatchCreate) =>
      create.mutateAsync({ ...payload, orderVariantId: activeVariant() }),
    updateSwatch: (variables: UpdateMaterialSwatchVariables) =>
      update.mutateAsync({ ...variables, orderVariantId: activeVariant() }),
    deleteSwatch: (id: string) =>
      remove.mutateAsync({ id, orderId: openOrder() }),
    assignSwatch: (variables: MaterialSwatchPartsVariables) =>
      assign.mutateAsync({
        ...variables,
        orderId: openOrder(),
        orderVariantId: activeVariant(),
      }),
    unassignSwatch: (variables: MaterialSwatchPartsVariables) =>
      unassign.mutateAsync({
        ...variables,
        orderId: openOrder(),
        orderVariantId: activeVariant(),
      }),
  };
}
